import React, { useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { useQuery } from 'react-query'
import { observer } from 'mobx-react'
import Container from 'presentation/hospital/components/Container'
import Heading from 'presentation/shared/components/Heading'
import CalendarIcon from 'presentation/assets/icons/calendar-v2.svg'
import SelectField from 'presentation/shared/components/SelectField'
import Button from 'presentation/shared/components/Button'
import TextField from 'presentation/shared/components/TextField'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import { useServices } from 'presentation/hooks/use-services'
import Table from 'presentation/shared/components/Table'
import Switch from 'presentation/shared/components/Switch'
import { useStores } from 'presentation/hooks/use-stores'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import StatusFilter from 'presentation/hospital/components/DoctorRegister/StatusFilter'
import PendenciesFilter from 'presentation/hospital/components/DoctorRegister/PendenciesFilter'
import {
  DoctorRegister,
  DoctorRegisterStatus
} from 'domain/entities/doctor-register'
import { DoctorRegisterStepNames } from 'domain/entities/doctor-register-step-names-model'
import { Profile } from 'common/enum/profile'
import { DoctorStatus } from 'common/enum/doctor-status'
import * as S from './styles'
import { queryParams, timeCourseOptions } from './data'
import { formatRecommendationToTable, getTableColumns } from './table'
import { Menu } from '@material-ui/core'
import { ConcludeRegisterModal } from '../../../../doctor/components/ConcludeRegisterModal'
import { Pagination } from 'service/protocols/api/api-client'

const HospitalHomeDoctorRegisterLayout = ({
  setIsLoading
}: WithLoadingProps) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [activeRegister, setActiveRegister] = React.useState<DoctorRegister>()
  const [showConcludeRegisterModal, setShowConcludeRegisterModal] =
    React.useState<boolean>(false)
  const [pagination, setPagination] = useState<Pagination>({
    pageNumber: 1,
    pageSize: 10
  })

  const [status, setStatus] = useState<DoctorRegisterStatus>()
  const [pendencyType, setPendencyType] = useState<DoctorRegisterStepNames>()

  const {
    handleChange,
    values: { name, timeCourse, prioritizeEmergency },
    setFieldValue,
    handleSubmit
  } = useFormik<SearchDoctorRegistersProps>({
    initialValues: {
      name: '',
      timeCourse: 'last-30-days',
      prioritizeEmergency: true
    },
    onSubmit: () => {
      if (pagination.pageNumber === 1) {
        refetch()
      } else {
        setPagination({
          ...pagination,
          pageNumber: 1
        })
      }
    }
  })

  const doctorService = useServices().doctor
  const currentHospital = useStores().currentHospital.actualHospital
  const userProfile = useStores().currentAccount.getCurrentAccount().user.role

  const history = useHistory()

  const { data, isLoading, refetch, remove } = useQuery(
    [
      'getAllRecommendedDoctors',
      {
        pagination,
        currentHospital,
        timeCourse,
        status,
        pendencyType,
        prioritizeEmergency
      }
    ],
    async () =>
      await doctorService.loadAllDoctorRegister({
        query: {
          startDate: getInitialDateByTimeCourse(timeCourse)
            ?.startOf('day')
            .toISOString(),
          endDate: moment().endOf('day').toISOString(),
          doctorNameOrCrm: name,
          pendencyType,
          status,
          prioritizeEmergency,
          viewSentDoctor: isDirector
        },
        pagination: {
          pageNumber: pagination.pageNumber ?? 1,
          pageSize: pagination.pageSize ?? 10
        },
        params: queryParams
      })
  )

  useEffect(() => {
    setIsLoading(isLoading)
  }, [isLoading])

  const isDirector = useMemo(
    () => userProfile === Profile.DIRECTOR_DOCTOR_REGISTER,
    [userProfile]
  )

  const handleMenuClick = (event: any, doctorRegister: DoctorRegister) => {
    setActiveRegister(doctorRegister)
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const actions = [
    {
      label: 'Concluir cadastro',
      handle: () => {
        handleMenuClose()
        setShowConcludeRegisterModal(true)
        refetch()
      }
    }
  ]

  const tableColumns = getTableColumns(isDirector)

  useEffect(() => {
    return () => {
      remove()
    }
  }, [])

  const RenderTable = useMemo(() => {
    return (
      <Table
        columns={tableColumns}
        data={
          data?.data.map((recommendation) => {
            return formatRecommendationToTable(recommendation, handleMenuClick)
          }) || []
        }
        onClick={(e) => {
          history.push(`/novos-cadastros/detalhes`, {
            doctor_id: e[1]
          })
        }}
        count={data?.pageInfo?.totalItems}
        rowsPerPage={data?.pageInfo?.itemsPerPage}
        page={data?.pageInfo?.currentPage}
        onPaginate={async (pageNumber, pageSize) => {
          setPagination({
            pageNumber,
            pageSize
          })
          return undefined
        }}
        styleOptions={{
          cellRelative: true,
          rowColor: '#FFF'
        }}
      />
    )
  }, [data, pagination, history])

  const getInitialDateByTimeCourse = (
    timeCourse: string | undefined
  ): moment.Moment | undefined => {
    if (!timeCourse) {
      return undefined
    }
    const today = moment().tz('America/Sao_Paulo')
    let initialDate: moment.Moment | undefined
    switch (timeCourse) {
      case 'today':
        initialDate = today
        break
      case 'last-7-days':
        initialDate = today.subtract('7', 'days')
        break
      case 'last-30-days':
        initialDate = today.subtract('30', 'days')
        break
      case 'last-trimester':
        initialDate = today.subtract('3', 'months')
        break
      case 'over-3-months':
        initialDate = undefined
        break
      default:
        initialDate = undefined
        break
    }
    return initialDate
  }

  return (
    <Container>
      <S.Wrapper>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {actions &&
            actions.map((action, index) => (
              <S.StyledMenuItem key={index} onClick={action.handle}>
                <span>{action.label}</span>
              </S.StyledMenuItem>
            ))}
        </Menu>
        <S.HeaderContainer>
          <Heading size="xlarge" color="primary">
            {isDirector ? 'Diretor Médico' : 'Corpo Clínico | Novos Cadastros'}
          </Heading>
          <S.HeaderActions>
            <S.Calendar>
              <img alt="calendário" src={CalendarIcon} />
            </S.Calendar>
            <SelectField
              items={timeCourseOptions}
              outline
              onChange={handleChange('timeCourse')}
              value={timeCourse || ''}
            />
          </S.HeaderActions>
        </S.HeaderContainer>
        <S.InputContainer onSubmit={handleSubmit}>
          <TextField
            name="name"
            id="name"
            bgColor="mainBg"
            placeholder="Busca pelo nome do profissional ou número do registro no conselho"
            iconPosition="left"
            icon={<SearchIcon />}
            iconMargin="50px"
            iconLocale="inside"
            onInputChange={handleChange('name')}
          />
          <Button type="submit">Buscar</Button>
        </S.InputContainer>
        <S.TableContainer>
          <S.TableHeaderFilters>
            <div style={{ display: 'flex', gap: '5px' }}>
              <StatusFilter
                onChange={(value: DoctorRegisterStatus) => setStatus(value)}
              />
              {!isDirector && (
                <PendenciesFilter
                  onChange={(value: DoctorRegisterStepNames) =>
                    setPendencyType(value)
                  }
                />
              )}
            </div>
            <S.EmmergencyFilter>
              <Switch
                handleSwitch={(checked) =>
                  setFieldValue('prioritizeEmergency', !checked)
                }
                isChecked={!prioritizeEmergency}
              />
              <label>Exibir do mais antigo para o mais novo</label>
            </S.EmmergencyFilter>
          </S.TableHeaderFilters>
          {RenderTable}
        </S.TableContainer>
      </S.Wrapper>
      {showConcludeRegisterModal && (
        <ConcludeRegisterModal
          close={() => setShowConcludeRegisterModal(false)}
          doctorRegister={activeRegister}
        />
      )}
    </Container>
  )
}

type SearchDoctorRegistersProps = {
  name?: string
  doctorStatus?: DoctorStatus
  prioritizeEmergency?: boolean
  timeCourse?: string
}

const component = observer(HospitalHomeDoctorRegisterLayout)
export default WithLoading(component)
